import {
  designCollectionProductNoun,
  designDeleteTargetKey,
  isDesignDeleteTargetCovered,
  toggleDesignDeleteTarget,
} from "@afar-research/afar";
import type {
  DesignArtifactGroup,
  DesignDeleteTarget,
  DesignSystemArtifactInventory,
  EnvironmentId,
} from "@afar-research/contracts";
import { useEffect, useMemo, useState } from "react";

import { projectEnvironment } from "../../state/projects";
import { useEnvironmentQuery } from "../../state/query";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";

/**
 * Picks what the Delete Design Operation removes: whole Design Systems, the
 * Design Artifact Groups inside them, and Design Systems the listing could not
 * read at all.
 *
 * Two levels rather than a flat list, because a flat list would force every
 * group row to restate its system — unreadable at a dozen systems — and would
 * hide the containment that lets a checked system disable its own group rows.
 *
 * Rows say what the filesystem can answer and nothing else. There is no Design
 * System State badge here: every surface that reported it is gone, and a health
 * badge on the delete picker is that surface creeping back in.
 */
export function DesignDeleteTargetsDialog(props: {
  readonly open: boolean;
  readonly environmentId: EnvironmentId;
  readonly projectRoot: string;
  readonly selected: ReadonlyArray<DesignDeleteTarget>;
  readonly onOpenChange: (open: boolean) => void;
  readonly onConfirm: (targets: ReadonlyArray<DesignDeleteTarget>) => void;
}) {
  return props.open ? <DesignDeleteTargetsDialogSession {...props} /> : null;
}

/** Templates, Examples, then Samples — the order a Design System is built in. */
const COLLECTION_ORDER = ["templates", "examples", "samples"] as const;

function groupsByCollection(
  inventory: DesignSystemArtifactInventory | undefined,
): ReadonlyArray<{ readonly noun: string; readonly groups: ReadonlyArray<DesignArtifactGroup> }> {
  if (inventory === undefined) return [];
  return COLLECTION_ORDER.flatMap((collection) => {
    const groups = inventory.artifactGroups.filter((group) => group.collection === collection);
    return groups.length === 0 ? [] : [{ noun: designCollectionProductNoun(collection), groups }];
  });
}

function fileCountText(count: number): string {
  return `${count} ${count === 1 ? "file" : "files"}`;
}

function DesignDeleteTargetsDialogSession(props: {
  readonly open: boolean;
  readonly environmentId: EnvironmentId;
  readonly projectRoot: string;
  readonly selected: ReadonlyArray<DesignDeleteTarget>;
  readonly onOpenChange: (open: boolean) => void;
  readonly onConfirm: (targets: ReadonlyArray<DesignDeleteTarget>) => void;
}) {
  const listing = useEnvironmentQuery(
    projectEnvironment.listDesignSystems({
      environmentId: props.environmentId,
      input: { cwd: props.projectRoot },
    }),
  );
  const [search, setSearch] = useState("");
  const [selection, setSelection] = useState<ReadonlyArray<DesignDeleteTarget>>(props.selected);
  // The picker opens on what the chip row already carries, so reopening it
  // edits the selection rather than starting over.
  useEffect(() => {
    setSelection(props.selected);
  }, [props.selected]);

  const inventoryBySlug = useMemo(
    () =>
      new Map(
        (listing.data?.artifactInventory ?? []).map((inventory) => [
          inventory.designSystemSlug,
          inventory,
        ]),
      ),
    [listing.data?.artifactInventory],
  );

  const needle = search.trim().toLowerCase();

  const systems = useMemo(() => {
    const matches = (...fields: ReadonlyArray<string>) =>
      needle.length === 0 || fields.some((field) => field.toLowerCase().includes(needle));
    return (listing.data?.systems ?? []).flatMap((system) => {
      const systemMatches = matches(system.name, system.slug);
      const sections = groupsByCollection(inventoryBySlug.get(system.slug)).flatMap((section) => {
        const groups = section.groups.filter(
          (group) => systemMatches || matches(section.noun, group.groupSlug),
        );
        return groups.length === 0 ? [] : [{ ...section, groups }];
      });
      // A system stays visible when the query matched it, even if no group did:
      // its own row is what the query was looking for.
      if (!systemMatches && sections.length === 0) return [];
      return [{ system, sections, fileCount: inventoryBySlug.get(system.slug)?.fileCount ?? 0 }];
    });
  }, [listing.data?.systems, inventoryBySlug, needle]);

  const invalidSystems = useMemo(
    () =>
      (listing.data?.invalidSystems ?? []).filter(
        (system) => needle.length === 0 || system.relativePath.toLowerCase().includes(needle),
      ),
    [listing.data?.invalidSystems, needle],
  );

  const selectedKeys = new Set(selection.map(designDeleteTargetKey));
  const toggle = (target: DesignDeleteTarget) =>
    setSelection((current) => toggleDesignDeleteTarget(current, target));

  const isEmpty = systems.length === 0 && invalidSystems.length === 0;

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogPopup data-design-delete-targets-dialog>
        <DialogHeader>
          <DialogTitle>Delete Design Systems and artifacts</DialogTitle>
          <DialogDescription>
            Checking a Design System covers everything inside it, so its own artifact groups stop
            being separately selectable.
          </DialogDescription>
        </DialogHeader>
        <DialogPanel>
          <div className="space-y-3">
            <Input
              value={search}
              placeholder="Filter Design Systems and artifacts…"
              aria-label="Filter Design Systems and artifacts"
              onChange={(event) => setSearch(event.target.value)}
            />
            {listing.error !== null ? (
              <div className="rounded-md border border-destructive/40 p-4 text-sm text-destructive">
                {listing.error}
              </div>
            ) : isEmpty ? (
              <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
                {listing.isPending
                  ? "Reading the Design Workspace…"
                  : needle.length > 0
                    ? "Nothing matches that filter."
                    : "This project has no Design Systems to delete."}
              </div>
            ) : (
              <div
                className="max-h-72 space-y-4 overflow-auto"
                aria-label="Design Systems and artifact groups"
              >
                {systems.map(({ system, sections, fileCount }) => {
                  const systemTarget: DesignDeleteTarget = {
                    kind: "system",
                    designSystemSlug: system.slug,
                    expectedRevision: system.revision,
                    name: system.name,
                  };
                  const systemChecked = selectedKeys.has(designDeleteTargetKey(systemTarget));
                  return (
                    <div key={system.slug} className="space-y-1">
                      <label className="flex w-full cursor-pointer items-center gap-3 rounded-md border p-2.5 text-left hover:bg-muted/50">
                        <Checkbox
                          checked={systemChecked}
                          onCheckedChange={() => toggle(systemTarget)}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium">
                            {system.name} · {system.slug}
                          </span>
                          <span className="block truncate text-xs text-muted-foreground">
                            {fileCountText(fileCount)}
                          </span>
                        </span>
                      </label>
                      {sections.map((section) => (
                        <div key={section.noun} className="ms-6 space-y-1">
                          <p className="pt-1 text-xs font-medium text-muted-foreground">
                            {section.noun}
                          </p>
                          <ul className="space-y-1">
                            {section.groups.map((group) => {
                              const target: DesignDeleteTarget = {
                                kind: "artifact-group",
                                designSystemSlug: system.slug,
                                expectedRevision: system.revision,
                                collection: group.collection,
                                groupSlug: group.groupSlug,
                              };
                              // Covered rows read as checked and refuse the
                              // click, so a redundant selection cannot be built.
                              const covered = isDesignDeleteTargetCovered(target, selection);
                              return (
                                <li key={group.groupSlug}>
                                  <label
                                    className={`flex w-full items-center gap-3 rounded-md border p-2 text-left ${
                                      covered
                                        ? "cursor-not-allowed opacity-60"
                                        : "cursor-pointer hover:bg-muted/50"
                                    }`}
                                  >
                                    <Checkbox
                                      checked={
                                        covered || selectedKeys.has(designDeleteTargetKey(target))
                                      }
                                      disabled={covered}
                                      onCheckedChange={() => {
                                        if (!covered) toggle(target);
                                      }}
                                    />
                                    <span className="min-w-0 flex-1 truncate text-sm">
                                      {group.groupSlug}{" "}
                                      <span className="text-xs text-muted-foreground">
                                        · {fileCountText(group.fileCount)}
                                      </span>
                                    </span>
                                  </label>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      ))}
                    </div>
                  );
                })}
                {invalidSystems.length > 0 ? (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground">
                      Unreadable Design Systems
                    </p>
                    <ul className="space-y-1">
                      {invalidSystems.map((system) => {
                        const target: DesignDeleteTarget = {
                          kind: "invalid-system",
                          relativePath: system.relativePath,
                        };
                        return (
                          <li key={system.relativePath}>
                            <label className="flex w-full cursor-pointer items-center gap-3 rounded-md border p-2.5 text-left hover:bg-muted/50">
                              <Checkbox
                                checked={selectedKeys.has(designDeleteTargetKey(target))}
                                onCheckedChange={() => toggle(target)}
                              />
                              <span className="min-w-0 flex-1">
                                <span className="block truncate text-sm font-medium">
                                  {system.relativePath}
                                </span>
                                <span className="block truncate text-xs text-muted-foreground">
                                  {system.detail}
                                </span>
                              </span>
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ) : null}
              </div>
            )}
            {/* Said once, here, rather than per row: a per-target git status
                would be a second surface reporting Design state, and it would
                be wrong the moment the agent writes a file. */}
            <p className="text-xs text-muted-foreground">
              Deleting removes files from disk and leaves the change unstaged. Committed work comes
              back with git; anything uncommitted does not.
            </p>
          </div>
        </DialogPanel>
        <DialogFooter>
          <Button variant="ghost" onClick={() => props.onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              props.onConfirm(selection);
              props.onOpenChange(false);
            }}
          >
            {selection.length > 0 ? `Select ${selection.length}` : "Select none"}
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
